import {
  UIIncident,
  UIIncidentSummary,
  UIIncidentHistory,
  LabeledCount,
  UIThirdPartyBreakdown,
} from '../../../../../../../api/teamMetricsApi';

const SEVERITY_ORDER = ['SEV 1', 'SEV 2', 'SEV 3', 'SEV 4'];

function normalizeFieldName(name: string): string {
  return name.toLowerCase().replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
}

function findField(fields: Record<string, string>, searchPattern: string): string | null {
  const normalizedPattern = normalizeFieldName(searchPattern);
  const key = Object.keys(fields).find(
    (k) => normalizeFieldName(k) === normalizedPattern ||
           normalizeFieldName(k).startsWith(normalizedPattern)
  );
  return key ? fields[key]?.trim() || null : null;
}

function findFieldStartingWith(fields: Record<string, string>, prefix: string): string | null {
  const normalizedPrefix = normalizeFieldName(prefix);
  const key = Object.keys(fields).find(
    (k) => normalizeFieldName(k).startsWith(normalizedPrefix)
  );
  return key ? fields[key]?.trim() || null : null;
}

function findDurationValue(fields: Record<string, string>): string | null {
  const durationKey = Object.keys(fields).find(
    (key) => key.toLowerCase().startsWith('duration')
  );
  return durationKey ? fields[durationKey] : null;
}

function parseMinutes(durationStr: string): number | null {
  if (!durationStr) return null;

  const hours = durationStr.match(/(\d+(?:\.\d+)?)\s*h/i);
  const mins = durationStr.match(/(\d+)\s*m/i);

  let total = 0;
  if (hours) total += parseFloat(hours[1]) * 60;
  if (mins) total += parseInt(mins[1], 10);

  return total > 0 ? total : null;
}

function parseTimeToMinutes(timeStr: string): number | null {
  if (!timeStr || timeStr === '0:00:00' || timeStr.trim() === '') return null;

  // Handle HH:MM:SS or H:MM:SS format (e.g., "17:23:00", "0:10:00")
  const hhmmss = timeStr.match(/^(\d+):(\d{2}):(\d{2})$/);
  if (hhmmss) {
    const hours = parseInt(hhmmss[1], 10);
    const mins = parseInt(hhmmss[2], 10);
    const secs = parseInt(hhmmss[3], 10);
    const total = hours * 60 + mins + secs / 60;
    return total > 0 ? total : null;
  }

  // Handle H:MM format (e.g., "2:29")
  const hhmm = timeStr.match(/^(\d+):(\d{2})$/);
  if (hhmm) {
    const hours = parseInt(hhmm[1], 10);
    const mins = parseInt(hhmm[2], 10);
    const total = hours * 60 + mins;
    return total > 0 ? total : null;
  }

  // Fall back to the existing parseMinutes logic
  return parseMinutes(timeStr);
}

export function countByField(incidents: UIIncident[], field: string): LabeledCount[] {
  const counts: Record<string, number> = {};
  incidents.forEach((inc) => {
    const value = findField(inc.fields, field);
    if (value) {
      counts[value] = (counts[value] || 0) + 1;
    }
  });
  return Object.entries(counts).map(([label, count]) => ({ label, count }));
}

export interface DetectionMethodPair {
  actual: string;
  preferred: string;
  count: number;
}

export interface PercentileValue {
  value: number;
  incidentName: string;
}

export interface IncidentTimeDataPoint {
  value: number;
  incidentName: string;
  severity: string | null;
  detectionMethod: string | null;
  spreadsheetRowLink: string;
}

export interface TimeDistribution {
  p50: PercentileValue | null;
  p90: PercentileValue | null;
  p95: PercentileValue | null;
  notRecorded: { count: number; incidentNames: string[] };
  total: number;
  recorded: number;
  dataPoints: IncidentTimeDataPoint[];
  median: number | null;
  mean: number | null;
}

export interface ResponseQualityMetrics {
  ttdMinutes: number | null;
  ttdDistribution: TimeDistribution;
  ttrMinutes: number | null;
  ttrDistribution: TimeDistribution;
  detectionMismatchCount: number;
  detectionMismatchTotal: number;
  detectionMismatches: DetectionMethodPair[];
  detectionMatches: DetectionMethodPair[];
  mttrBySeverity: Record<string, number | null>;
}


function parseTimestampDiff(startStr: string | null, endStr: string | null): number | null {
  if (!startStr || !endStr) return null;
  const start = new Date(startStr);
  const end = new Date(endStr);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
  const diffMs = end.getTime() - start.getTime();
  if (diffMs < 0) return null;
  return diffMs / (1000 * 60);
}

export function computeResponseQualityMetrics(incidents: UIIncident[]): ResponseQualityMetrics {
  let detectionMismatchCount = 0;
  let detectionMismatchTotal = 0;
  const mismatchPairs: Map<string, number> = new Map();
  const matchPairs: Map<string, number> = new Map();
  const mttrBySev: Record<string, number[]> = {};

  // Track TTD and TTR values with full incident data for dot plot
  const ttdDataPoints: IncidentTimeDataPoint[] = [];
  const ttrDataPoints: IncidentTimeDataPoint[] = [];
  const ttdNotRecorded: string[] = [];
  const ttrNotRecorded: string[] = [];
  let ttdTotal = 0;
  let ttrTotal = 0;

  SEVERITY_ORDER.forEach((sev) => {
    mttrBySev[sev] = [];
  });

  incidents.forEach((inc) => {
    const incidentName = findFieldStartingWith(inc.fields, 'Description') ||
                        findFieldStartingWith(inc.fields, 'Link to Incident Report') ||
                        'Unknown Incident';
    const severity = findFieldStartingWith(inc.fields, 'SEV Level V2');
    const detectionMethod = findFieldStartingWith(inc.fields, 'Detection Method');
    const spreadsheetRowLink = inc.spreadsheetRowLink || '';

    // Look for pre-calculated TTD field (the tracker already calculates this)
    // Field name is "Time to Detection (calculated)"
    const ttdField = findFieldStartingWith(inc.fields, 'Time to Detection');
    let ttd = ttdField ? parseTimeToMinutes(ttdField) : null;

    // If no pre-calculated field, try computing from timestamps
    if (ttd === null) {
      const startDate = findFieldStartingWith(inc.fields, 'Start Date');
      const detectionTimestamp = findFieldStartingWith(inc.fields, 'Detection Timestamp') ||
                                 findFieldStartingWith(inc.fields, 'Detection Time') ||
                                 findFieldStartingWith(inc.fields, 'Incident Detection');
      ttd = parseTimestampDiff(startDate, detectionTimestamp);
    }

    // Track TTD with full incident data
    ttdTotal++;
    if (ttd === null) {
      ttdNotRecorded.push(incidentName);
    } else {
      ttdDataPoints.push({ value: ttd, incidentName, severity, detectionMethod, spreadsheetRowLink });
    }

    // Look for pre-calculated TTR field or compute from timestamps
    // Field name is "Response Time (calculated)"
    const ttrField = findFieldStartingWith(inc.fields, 'Response Time');
    let ttr = ttrField ? parseTimeToMinutes(ttrField) : null;

    if (ttr === null) {
      const detectionTimestamp = findFieldStartingWith(inc.fields, 'Detection Timestamp') ||
                                 findFieldStartingWith(inc.fields, 'Incident Detection');
      const responseTimestamp = findFieldStartingWith(inc.fields, 'Response Initiated') ||
                                findFieldStartingWith(inc.fields, 'Response Time') ||
                                findFieldStartingWith(inc.fields, 'Response Start');
      ttr = parseTimestampDiff(detectionTimestamp, responseTimestamp);
    }

    // Track TTR with full incident data
    ttrTotal++;
    if (ttr === null) {
      ttrNotRecorded.push(incidentName);
    } else {
      ttrDataPoints.push({ value: ttr, incidentName, severity, detectionMethod, spreadsheetRowLink });
    }

    // Detection Gap: "Customer Reported" and "Internal Report" are gaps (we should have detected proactively)
    // All other detection methods (Alert, External Notification, Monitoring Deployment, Vulnerability Scanning) are good
    // Empty/missing values are also considered gaps
    detectionMismatchTotal++;
    if (!detectionMethod) {
      // No detection method recorded - count as a gap
      detectionMismatchCount++;
      const label = '(Not recorded)';
      mismatchPairs.set(label, (mismatchPairs.get(label) || 0) + 1);
    } else {
      const normalizedMethod = detectionMethod.toLowerCase().trim();
      const isGap = normalizedMethod === 'customer reported' || normalizedMethod === 'internal report';
      if (isGap) {
        detectionMismatchCount++;
        mismatchPairs.set(detectionMethod, (mismatchPairs.get(detectionMethod) || 0) + 1);
      } else {
        matchPairs.set(detectionMethod, (matchPairs.get(detectionMethod) || 0) + 1);
      }
    }

    const durationStr = findDurationValue(inc.fields) || '';
    const duration = parseTimeToMinutes(durationStr) ?? parseMinutes(durationStr);
    if (severity && SEVERITY_ORDER.includes(severity) && duration !== null) {
      mttrBySev[severity].push(duration);
    }
  });

  const mttrBySeverity: Record<string, number | null> = {};
  SEVERITY_ORDER.forEach((sev) => {
    const values = mttrBySev[sev];
    mttrBySeverity[sev] = values.length > 0
      ? values.reduce((a, b) => a + b, 0) / values.length
      : null;
  });

  const detectionMismatches: DetectionMethodPair[] = Array.from(mismatchPairs.entries())
    .map(([method, count]) => ({ actual: method, preferred: '', count }))
    .sort((a, b) => b.count - a.count);

  const detectionMatches: DetectionMethodPair[] = Array.from(matchPairs.entries())
    .map(([method, count]) => ({ actual: method, preferred: '', count }))
    .sort((a, b) => b.count - a.count);

  // Helper to compute percentile value with incident name
  const getPercentile = (
    sorted: IncidentTimeDataPoint[],
    percentile: number
  ): PercentileValue | null => {
    if (sorted.length === 0) return null;
    const index = Math.ceil((percentile / 100) * sorted.length) - 1;
    const clampedIndex = Math.max(0, Math.min(index, sorted.length - 1));
    return { value: sorted[clampedIndex].value, incidentName: sorted[clampedIndex].incidentName };
  };

  // Helper to compute median
  const getMedian = (sorted: IncidentTimeDataPoint[]): number | null => {
    if (sorted.length === 0) return null;
    const mid = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 0) {
      return (sorted[mid - 1].value + sorted[mid].value) / 2;
    }
    return sorted[mid].value;
  };

  // Compute TTD distribution (p50, p90, p95) with data points
  const sortedTtd = [...ttdDataPoints].sort((a, b) => a.value - b.value);
  const ttdMean = ttdDataPoints.length > 0
    ? ttdDataPoints.reduce((a, b) => a + b.value, 0) / ttdDataPoints.length
    : null;
  const ttdDistribution: TimeDistribution = {
    p50: getPercentile(sortedTtd, 50),
    p90: getPercentile(sortedTtd, 90),
    p95: getPercentile(sortedTtd, 95),
    notRecorded: { count: ttdNotRecorded.length, incidentNames: ttdNotRecorded },
    total: ttdTotal,
    recorded: ttdDataPoints.length,
    dataPoints: ttdDataPoints,
    median: getMedian(sortedTtd),
    mean: ttdMean,
  };

  // Compute TTR distribution (p50, p90, p95) with data points
  const sortedTtr = [...ttrDataPoints].sort((a, b) => a.value - b.value);
  const ttrMean = ttrDataPoints.length > 0
    ? ttrDataPoints.reduce((a, b) => a + b.value, 0) / ttrDataPoints.length
    : null;
  const ttrDistribution: TimeDistribution = {
    p50: getPercentile(sortedTtr, 50),
    p90: getPercentile(sortedTtr, 90),
    p95: getPercentile(sortedTtr, 95),
    notRecorded: { count: ttrNotRecorded.length, incidentNames: ttrNotRecorded },
    total: ttrTotal,
    recorded: ttrDataPoints.length,
    dataPoints: ttrDataPoints,
    median: getMedian(sortedTtr),
    mean: ttrMean,
  };

  return {
    ttdMinutes: ttdMean,
    ttdDistribution,
    ttrMinutes: ttrMean,
    ttrDistribution,
    detectionMismatchCount,
    detectionMismatchTotal,
    detectionMismatches,
    detectionMatches,
    mttrBySeverity,
  };
}

function computeThirdPartyBreakdown(incidents: UIIncident[]): UIThirdPartyBreakdown {
  let thirdParty = 0;
  let internal = 0;
  let unknown = 0;

  incidents.forEach((inc) => {
    const value = findFieldStartingWith(inc.fields, '3rd Party')?.toLowerCase();
    if (value === 'yes' || value === 'true') {
      thirdParty++;
    } else if (value === 'no' || value === 'false') {
      internal++;
    } else {
      unknown++;
    }
  });

  return { thirdParty, internal, unknown };
}

function groupByMonth(incidents: UIIncident[]): Map<string, UIIncident[]> {
  const groups = new Map<string, UIIncident[]>();

  incidents.forEach((inc) => {
    const dateStr = findFieldStartingWith(inc.fields, 'Start Date');
    if (!dateStr) return;

    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return;

    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    if (!groups.has(monthKey)) {
      groups.set(monthKey, []);
    }
    groups.get(monthKey)!.push(inc);
  });

  return groups;
}

export function computeIncidentHistory(
  incidents: UIIncident[],
  startDate: string
): UIIncidentHistory[] {
  const monthGroups = groupByMonth(incidents);
  const history: UIIncidentHistory[] = [];

  const dateParts = startDate.split('-').map(Number);
  const startYear = dateParts[0];
  const startMonth = dateParts[1] - 1;

  const now = new Date();
  let year = startYear;
  let month = startMonth;

  while (year < now.getFullYear() || (year === now.getFullYear() && month <= now.getMonth())) {
    const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
    const monthIncidents = monthGroups.get(monthKey) || [];

    const durations = monthIncidents
      .map((inc) => parseMinutes(findDurationValue(inc.fields) || ''))
      .filter((d): d is number => d !== null);

    const mttr =
      durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : null;

    history.push({
      date: monthKey,
      incidentCount: monthIncidents.length || null,
      totalDurationMinutes: durations.length > 0 ? durations.reduce((a, b) => a + b, 0) : null,
      mttrMinutes: mttr,
      totalSupportCalls: null,
      severityDistribution: countByField(monthIncidents, 'SEV Level V2'),
      criticalityDistribution: countByField(monthIncidents, 'Criticality'),
      productImpactDistribution: countByField(monthIncidents, 'Product Impact Level'),
      thirdPartyBreakdown: computeThirdPartyBreakdown(monthIncidents),
    });

    month++;
    if (month > 11) {
      month = 0;
      year++;
    }
  }

  return history;
}

export function aggregateIncidents(
  incidents: UIIncident[],
  columnHeaders: string[],
  startDate: string
): UIIncidentSummary {
  const durations = incidents
    .map((inc) => parseMinutes(findDurationValue(inc.fields) || ''))
    .filter((d): d is number => d !== null);

  const mttrMinutes =
    durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : null;

  return {
    totalIncidents: incidents.length,
    mttrMinutes,
    totalSupportCalls: null,
    severityDistribution: countByField(incidents, 'SEV Level V2'),
    criticalityDistribution: countByField(incidents, 'Criticality'),
    productImpactDistribution: countByField(incidents, 'Product Impact Level'),
    thirdPartyBreakdown: computeThirdPartyBreakdown(incidents),
    incidentHistory: computeIncidentHistory(incidents, startDate),
    incidents,
    incidentColumnHeaders: columnHeaders,
  };
}
